import {useState} from "react";
import {useSelector} from "react-redux";
import ReportDashboard from "./report-dashboard";
import {
    normalizeMembershipEntity,
    prepareMembershipPayload,
} from "../workspace-membership/workspace-membership-identity";

const dashboardPopulate = {
    owner: {populate: ["member"]},
    archivedBy: {populate: {member: {populate: {profileImage: true}}}},
    workspace: true,
    reports: {
        filters: {
            $and: [
                {archivedAt: {$null: true}},
            ],
        },
        populate: {
            creatorUser: {populate: {member: {populate: {profileImage: true}}}},
            archivedBy: {populate: {member: {populate: {profileImage: true}}}},
        },
        sort: ["updatedAt:desc", "createdAt:desc"],
    },
};

export function useReportDashboard() {
    const [loading, setLoading] = useState(false);
    const workspaceMembership = useSelector((state) => state.app?.workspaceMembershipData);
    const memberships = workspaceMembership?.workspace?.workspace_memberships || [];

    const normalizeDashboard = (item) => {
        const normalized = normalizeMembershipEntity(item, "reportDashboard", memberships);
        if (!normalized) return normalized;
        normalized.reports = (normalized.reports || []).map((report) =>
            normalizeMembershipEntity(report, "report", memberships));
        return normalized;
    };

    const getReportDashboards = async (workspaceId) => {
        if (!workspaceId) return [];
        setLoading(true);
        try {
            const reportDashboard = new ReportDashboard();
            const query = {
                filters: {
                    $and: [
                        {workspace: {$eq: workspaceId}},
                        {archivedAt: {$null: true}},
                    ],
                },
                populate: dashboardPopulate,
                sort: ["updatedAt:desc", "createdAt:desc"],
            };
            const data = await reportDashboard.getAll(query);
            return (data.data || []).map(normalizeDashboard);
        } catch (error) {
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const getReportDashboard = async (documentId) => {
        if (!documentId) return null;
        setLoading(true);
        try {
            const reportDashboard = new ReportDashboard();
            const data = await reportDashboard.getById(documentId, {
                populate: dashboardPopulate,
            });
            return normalizeDashboard(data.data);
        } catch (error) {
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const createReportDashboard = async (dashboardData) => {
        setLoading(true);
        try {
            const reportDashboard = new ReportDashboard();
            const payload = prepareMembershipPayload("reportDashboard", dashboardData, memberships, {
                currentMembership: workspaceMembership,
                operation: "create",
            });
            const data = await reportDashboard.create(payload, {
                populate: dashboardPopulate,
            });
            return normalizeDashboard(data.data);
        } catch (error) {
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const updateReportDashboard = async (dashboardId, dashboardData) => {
        if (!dashboardId) return null;
        setLoading(true);
        try {
            const reportDashboard = new ReportDashboard();
            const payload = prepareMembershipPayload("reportDashboard", dashboardData, memberships, {
                currentMembership: workspaceMembership,
                operation: "update",
            });
            const data = await reportDashboard.update(dashboardId, payload, {
                populate: dashboardPopulate,
            });
            return normalizeDashboard(data.data);
        } catch (error) {
            throw error;
        } finally {
            setLoading(false);
        }
    };

    return {
        loading,
        getReportDashboards,
        getReportDashboard,
        createReportDashboard,
        updateReportDashboard,
    };
}
